import { type ComponentProps, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Button } from '@repo/ui/Button';
import { Checkbox } from '@repo/ui/Checkbox';
import { Dialog } from '@repo/ui/Dialog';
import { DialogClose } from '@repo/ui/Dialog/DialogClose';
import { DialogContent } from '@repo/ui/Dialog/DialogContent';
import { DialogFooter } from '@repo/ui/Dialog/DialogFooter';
import { DialogHeader } from '@repo/ui/Dialog/DialogHeader';
import { DialogRoot } from '@repo/ui/Dialog/DialogRoot';
import { DialogTitle } from '@repo/ui/Dialog/DialogTitle';
import { DialogTrigger } from '@repo/ui/Dialog/DialogTrigger';
import { Flex } from '@repo/ui/Flex';
import { Icon } from '@repo/ui/Icon';
import { IconButton } from '@repo/ui/IconButton';

import { ItemCard } from '../../components/ItemCard/ItemCard';
import { Text, rawString } from '../../components/Language/Language';
import type { OrderItem } from '../../graphql/query/getOrder';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { useShipmentMetadata } from '../../hooks/useShipmentGroups';
import { useWorkflow } from '../../workflow/hooks';
import { type WorkflowData, type WorkflowItemData } from '../../workflow';
import { getTrackingNumberFromGroups } from '../../utils/trackingNumberUtils';
import { makeItemKey, parseItemKey } from '../../utils/itemKey';

export interface AddMoreItemsDialogProps {
  isDisabled?: boolean;
  items: OrderItem[];
  disabledItems?: OrderItem[];
}

import s from './AddMoreItemsDialog.module.css';

export const AddMoreItemsDialog = (props: AddMoreItemsDialogProps) => {
  const { isDisabled = true, items, disabledItems = [] } = props;
  const { data, updateData } = useWorkflow();
  const { shipmentGroups } = useShipmentMetadata();
  const isMobile = useIsMobile();

  const { setValue } = useFormContext<WorkflowData>();

  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const allItemsSelected = selectedItems.length === items.length;

  useEffect(() => {
    if (selectedItems.length > 0 && showErrorMessage) {
      setShowErrorMessage(false);
    }
  }, [selectedItems, showErrorMessage]);

  const handleClose = () => {
    setSelectedItems([]);
    setShowErrorMessage(false);
  };

  const handleCheck: ComponentProps<'input'>['onChange'] = (e) => {
    const uniqueName = e.target.name;
    if (!uniqueName) return;

    setSelectedItems((prevSelected) => {
      if (e.target.checked) {
        return [...prevSelected, uniqueName];
      } else {
        return prevSelected.filter((id) => id !== uniqueName);
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedItems((_prevSelected) => {
      if (allItemsSelected) {
        return [];
      } else {
        return items.map((item) => {
          const trackingNumber =
            (item as unknown as { _shipmentTrackingNumber?: string })
              ?._shipmentTrackingNumber ||
            getTrackingNumberFromGroups(shipmentGroups, item.id, item);
          return makeItemKey({ id: item.id, trackingNumber });
        });
      }
    });
  };

  const handleSubmit = () => {
    if (selectedItems.length === 0) {
      setShowErrorMessage(true);
      return false;
    }

    const newItems = selectedItems.reduce(
      (acc: Record<string, WorkflowItemData>, uniqueId: string) => {
        const { id: originalItemId, trackingNumber } = parseItemKey(uniqueId);

        const originalItem = items.find((item) => item.id === originalItemId);
        if (!originalItem) return acc;

        const itemSelection: WorkflowItemData['itemSelection'] = {
          id: originalItemId,
          quantity: 1,
          ...(trackingNumber && { trackingNumber }),
          sourceItemId: originalItemId,
        };

        acc[uniqueId] = {
          itemSelection,
        };
        return acc;
      },
      {},
    );

    if (data?.items) {
      const updatedItems = {
        ...data.items,
        ...newItems,
      };

      setValue('items', updatedItems);

      const updatedData = {
        items: updatedItems,
      };

      updateData(updatedData);
    }
    handleClose();
  };

  const buttonTextKey =
    selectedItems.length === 0
      ? 'pdx.item_selection.add.default'
      : selectedItems.length > 1
        ? 'pdx.item_selection.add.many'
        : 'pdx.item_selection.add.one';

  return (
    <DialogRoot>
      <DialogTrigger asChild={true}>
        <Button
          disabled={isDisabled}
          type="button"
          size={isMobile ? 'md' : 'lg'}
          variant="secondary"
          className={s.button}
        >
          <Icon name="plus-circle" size="md" />
          <Text langKey="pdx.reason_selection.add_another_item_button" />
        </Button>
      </DialogTrigger>
      <Dialog data-testid="addMoreItemsDialog">
        <DialogHeader className={s.header}>
          <DialogTitle position="center">
            <Text langKey="pdx.item_selection.add.title" />
          </DialogTitle>
          <DialogClose asChild={true} onClick={handleClose}>
            <IconButton
              icon="close"
              label={<Text langKey="pdx.global.close" />}
              variant="tertiary"
            />
          </DialogClose>
        </DialogHeader>
        <DialogContent className={s.content}>
          <Flex direction="column" gap="g-24">
            <section className={s.section}>
              <div className={s.sectionHeader}>
                <label className={s.selectAll} htmlFor="selectAll">
                  <Checkbox
                    checked={allItemsSelected}
                    name="selectAll"
                    id="selectAll"
                    onChange={handleSelectAll}
                  />
                  <Text langKey="pdx.item_selection.add.select_all" />
                </label>
              </div>
              {items.map((item) => {
                const { id, image, title } = item;
                const trackingNumber =
                  (item as unknown as { _shipmentTrackingNumber?: string })
                    ?._shipmentTrackingNumber ||
                  getTrackingNumberFromGroups(shipmentGroups, item.id, item);
                const uniqueId = makeItemKey({ id, trackingNumber });

                return (
                  <label
                    aria-labelledby={`${uniqueId}-label`}
                    htmlFor={uniqueId}
                    className={s.checkboxCard}
                    key={uniqueId}
                    data-testid={'addMoreItem'}
                  >
                    <Checkbox
                      checked={selectedItems.includes(uniqueId)}
                      className={s.checkbox}
                      id={uniqueId}
                      name={uniqueId}
                      onChange={handleCheck}
                    />
                    <ItemCard
                      className={s.itemCard}
                      containerClassName={s.itemCardWrapper}
                      image={image?.url ?? ''}
                      name={title}
                      item={item}
                    />
                  </label>
                );
              })}
              {disabledItems.map((item) => {
                const { id, image, title } = item;
                const trackingNumber =
                  (item as unknown as { _shipmentTrackingNumber?: string })
                    ?._shipmentTrackingNumber ||
                  getTrackingNumberFromGroups(shipmentGroups, item.id, item);
                const uniqueId = `disabled-${makeItemKey({ id, trackingNumber })}`;
                const disabledLabelId = `disabled-label-${uniqueId}`;

                return (
                  <div
                    className={`${s.checkboxCard} ${s.disabledItem}`}
                    key={uniqueId}
                    role="group"
                    aria-labelledby={`${uniqueId}-label`}
                    aria-describedby={disabledLabelId}
                  >
                    <div
                      className={s.checkbox}
                      role="img"
                      aria-label={
                        rawString({
                          langKey: 'pdx.item_selection.add.auto_added',
                        }) ?? 'Will be added automatically'
                      }
                    >
                      <div className={s.disabledCheckboxIcon} />
                    </div>
                    <ItemCard
                      className={`${s.itemCard} ${s.disabledItemCard}`}
                      containerClassName={s.itemCardWrapper}
                      image={image?.url ?? ''}
                      name={title}
                      item={item}
                    />
                    <div
                      className={s.disabledLabel}
                      id={disabledLabelId}
                      role="note"
                    >
                      <Text langKey="pdx.item_selection.add.auto_added" />
                    </div>
                  </div>
                );
              })}
            </section>
            {showErrorMessage && (
              <div className={s.error}>
                <Icon name="warning" size="font" />
                <p aria-live="assertive">
                  <Text langKey="pdx.item_selection.add.error.no_item_selected" />
                </p>
              </div>
            )}
          </Flex>
        </DialogContent>
        <DialogFooter className={s.footer}>
          <DialogClose asChild={true} disabled={selectedItems.length === 0}>
            <Button
              ariaDisabled={selectedItems.length === 0}
              size="lg"
              type="button"
              variant="primary"
              isFullWidth
              onClick={handleSubmit}
            >
              <Text
                langKey={buttonTextKey}
                replacementTokens={{
                  item_count: selectedItems.length.toString(),
                }}
              />
            </Button>
          </DialogClose>
        </DialogFooter>
      </Dialog>
    </DialogRoot>
  );
};
